import React from 'react';
import { PATTERN_TEMPLATES } from '../utils/patternTemplates';

interface PatternSparklineProps {
  patternType: string;
  width?: number;
  height?: number;
  className?: string;
}

// Convert key points to an SVG polyline points string, fitting within the given dimensions.
function toSvgPoints(
  rawPoints: [number, number][],
  invert: boolean,
  svgWidth: number,
  svgHeight: number,
  padding: number
): string {
  const ys = rawPoints.map(p => p[1]);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const yRange = maxY - minY || 1;

  const w = svgWidth - padding * 2;
  const h = svgHeight - padding * 2;

  return rawPoints
    .map(([x, y]) => {
      const normY = (y - minY) / yRange;
      // SVG y-axis is inverted (0 = top). invert flips high <-> low.
      const svgY = invert
        ? padding + normY * h
        : padding + (1 - normY) * h;
      return `${padding + x * w},${svgY.toFixed(2)}`;
    })
    .join(' ');
}

export function PatternSparkline({
  patternType,
  width = 80,
  height = 40,
  className,
}: PatternSparklineProps) {
  const template = (PATTERN_TEMPLATES as Record<string, typeof PATTERN_TEMPLATES[keyof typeof PATTERN_TEMPLATES] | undefined>)[patternType];
  if (!template) return null;

  const { points, invert = false, sentiment } = template;
  const padding = 3;
  const svgPoints = toSvgPoints(points, invert, width, height, padding);
  const color = sentiment === 'bullish' ? '#26a69a' : '#ef5350';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
    >
      <polyline
        points={svgPoints}
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
